import React, { useState, useEffect } from 'react';
import { getOrganizationRecommendations, getUserContext } from '../../services/api';

const OrganizationInsights = ({ className = "" }) => {
  const [insights, setInsights] = useState(null);
  const [userContext, setUserContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadInsights();
  }, []);

  const loadInsights = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load both recommendations and user context
      const [recommendationsData, contextData] = await Promise.all([
        getOrganizationRecommendations(),
        getUserContext()
      ]);
      
      setInsights(recommendationsData);
      setUserContext(contextData);
    } catch (err) {
      setError(`Failed to load insights: ${err.message}`);
      console.error('Error loading insights:', err);
    } finally {
      setLoading(false);
    }
  };

  const refreshInsights = async () => {
    try {
      setRefreshing(true);
      await loadInsights();
    } catch (err) {
      alert(`Failed to refresh insights: ${err.message}`);
    } finally {
      setRefreshing(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    if (score >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  const getScoreBackground = (score) => {
    if (score >= 80) return 'from-green-500 to-green-600';
    if (score >= 60) return 'from-yellow-500 to-yellow-600';
    if (score >= 40) return 'from-orange-500 to-orange-600';
    return 'from-red-500 to-red-600';
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'High': return 'fas fa-exclamation-triangle text-red-500';
      case 'Medium': return 'fas fa-exclamation-circle text-yellow-500';
      case 'Low': return 'fas fa-info-circle text-blue-500';
      default: return 'fas fa-info-circle text-gray-500';
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Easy': return 'text-green-600 bg-green-100 dark:bg-green-900 dark:text-green-300';
      case 'Medium': return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900 dark:text-yellow-300';
      case 'Hard': return 'text-red-600 bg-red-100 dark:bg-red-900 dark:text-red-300';
      default: return 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-400"></div>
          <span className="text-gray-600 dark:text-gray-400">Loading organization insights...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-4 ${className}`}>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <i className="fas fa-exclamation-triangle text-red-400"></i>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading insights</h3>
              <p className="mt-2 text-sm text-red-700">{error}</p>
              <button 
                onClick={loadInsights}
                className="mt-2 text-sm text-red-800 underline hover:text-red-900"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentScore = insights?.context?.current_score || 0;
  const recommendations = insights?.recommendations;

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Organization Insights
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            AI-powered recommendations to improve your file organization
          </p>
        </div>
        <button
          onClick={refreshInsights}
          disabled={refreshing}
          className="inline-flex items-center px-4 py-2 text-sm font-medium leading-normal bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {refreshing ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
              Refreshing...
            </>
          ) : (
            <>
              <i className="fas fa-sync mr-2"></i>
              Refresh
            </>
          )}
        </button>
      </div>

      {/* Organization Score */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-medium text-gray-900 dark:text-white">Organization Score</h4>
          <span className={`text-2xl font-bold ${getScoreColor(currentScore)}`}>
            {currentScore}/100
          </span>
        </div>
        
        {/* Score Bar */}
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 mb-4">
          <div 
            className={`h-3 rounded-full bg-gradient-to-r ${getScoreBackground(currentScore)} transition-all duration-500`}
            style={{ width: `${currentScore}%` }}
          ></div>
        </div>
        
        {/* Score Category */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600 dark:text-gray-400">
            Category: <span className="font-medium">{recommendations?.score_category || 'Unknown'}</span>
          </span>
          {recommendations?.potential_score_improvement && (
            <span className="text-blue-600 dark:text-blue-400">
              Potential: +{recommendations.potential_score_improvement} points
            </span>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      {userContext && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 text-center">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {userContext.total_files || 0}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Total Files</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 text-center">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {userContext.tags?.total_tags || 0}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Tags Created</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 text-center">
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
              {userContext.templates?.total_templates || 0}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Templates</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 text-center">
            <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
              {userContext.tags?.untagged_files || 0}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Untagged</div>
          </div>
        </div>
      )}

      {/* Recommendations */}
      {recommendations?.top_recommendations && recommendations.top_recommendations.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Top Recommendations
          </h4>
          
          <div className="space-y-4">
            {recommendations.top_recommendations.map((rec, index) => (
              <div 
                key={index}
                className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition duration-200"
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-1">
                    <i className={getPriorityIcon(rec.priority)}></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="font-medium text-gray-900 dark:text-white">
                        {rec.action}
                      </h5>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getDifficultyColor(rec.difficulty)}`}>
                        {rec.difficulty}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      {rec.impact}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-medium ${
                        rec.priority === 'High' ? 'text-red-600' :
                        rec.priority === 'Medium' ? 'text-yellow-600' :
                        'text-blue-600'
                      }`}>
                        {rec.priority} Priority
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Next Steps */}
      {recommendations?.next_steps && recommendations.next_steps.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Suggested Next Steps
          </h4>
          
          <div className="space-y-3">
            {recommendations.next_steps.map((step, index) => (
              <div key={index} className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-6 h-6 bg-red-400 text-white rounded-full flex items-center justify-center text-xs font-bold mt-0.5">
                  {index + 1}
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-300 flex-1">
                  {step}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Recommendations */}
      {(!recommendations?.top_recommendations || recommendations.top_recommendations.length === 0) && (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-8 border border-gray-200 dark:border-gray-700 text-center">
          <i className="fas fa-trophy text-4xl text-green-500 mb-4"></i>
          <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Great Job!
          </h4>
          <p className="text-gray-600 dark:text-gray-400">
            Your files are well organized. Keep up the good work!
          </p>
        </div>
      )}

      {/* Tips */}
      <div className="bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <i className="fas fa-lightbulb text-blue-400"></i>
          </div>
          <div className="ml-3">
            <h5 className="text-sm font-medium text-blue-800 dark:text-blue-200">
              Pro Tips
            </h5>
            <div className="mt-2 text-sm text-blue-700 dark:text-blue-300">
              <ul className="list-disc list-inside space-y-1">
                <li>Use consistent naming patterns for similar files</li>
                <li>Create tags for frequently used categories</li>
                <li>Set up auto-apply templates for new uploads</li>
                <li>Review and update your organization system regularly</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganizationInsights;



















































